/**
 * Debug Mode kill-odds readout. Every figure comes from `killOdds` (exact dynamic program over
 * held dice and rolls left) using only public information: face-up cards and held dice.
 *
 * Each seat shows, for every other player: the odds this seat's player kills them in one turn,
 * then the odds they kill this seat's player, each as 'natural / with power'. Click it for the
 * same numbers across every pass left in the round. Self-roll cards (power = a dice combo that
 * earns free kills) also show the odds of rolling their own combo.
 */
import { MAX_SEQUENCES, killOdds, type CardDef, type GameView, type PlayerId, type PublicPlayer } from '@kadice/shared';
import { PName } from './Names';

/** Powers earned by rolling their own combination: the dice that kill the card also give free kills. */
const SELF_ROLL = new Set(['xxxx', 'seq5', 'e', 'o', 'kadice']);

export interface OddsContext {
  view: GameView;
  catalog: ReadonlyMap<number, CardDef>;
  /** Player ids in turn order for this round (dealer first). */
  order: readonly PlayerId[];
  /** Your dice kept so far this roll: they stand in for your held dice, so the odds follow your choices. */
  kept: readonly number[] | null;
}

const cardOf = (cx: OddsContext, p: PublicPlayer) => (p.card === null ? undefined : cx.catalog.get(p.card));

/** Odds only exist while play is on, between two players whose cards are both face up. */
export const oddsReady = (cx: OddsContext) =>
  cx.view.phase === 'playing' && cx.view.turn !== null && !cx.view.turn.ghost;

/** Has this player already used their turn this pass (a roll already made counts as used)? */
function hasGone(cx: OddsContext, p: PublicPlayer): boolean {
  const turn = cx.view.turn;
  if (!turn) return false;
  const at = cx.order.indexOf(p.id);
  const now = cx.order.indexOf(turn.playerId);
  return at < now || (at === now && turn.hasRolled);
}

/** Dice this player starts their next roll with: your live selection on your turn, otherwise what's held. */
const heldOf = (cx: OddsContext, p: PublicPlayer): readonly number[] =>
  p.id === cx.view.you.id && cx.kept ? cx.kept : p.held;

/** Rolls this player gets by the end of `pass` counting from now (0 when they have no turn left by then). */
function rollsBy(cx: OddsContext, p: PublicPlayer, pass: number): number {
  const gone = hasGone(cx, p);
  const first = cx.view.sequence + (gone ? 1 : 0);
  if (pass < first || p.status !== 'active') return 0;
  // Small Straight's second first-pass turn is still to come unless this is it.
  const turn = cx.view.turn;
  const second = turn?.playerId === p.id && turn.extra;
  const bonus = cardOf(cx, p)?.power === 'seq4' && cx.view.sequence === 1 && !second ? 1 : 0;
  return pass - first + 1 + bonus;
}

interface Pair {
  natural: number;
  power: number;
}

function pairOdds(cx: OddsContext, attacker: PublicPlayer, target: PublicPlayer, pass: number): Pair | null {
  const a = cardOf(cx, attacker);
  const t = cardOf(cx, target);
  const rolls = rollsBy(cx, attacker, pass);
  if (!a || !t || rolls === 0) return null;
  return {
    natural: killOdds(t.killer, null, rolls, heldOf(cx, attacker)),
    power: killOdds(t.killer, a.power, rolls, heldOf(cx, attacker)),
  };
}

function selfRollOdds(cx: OddsContext, p: PublicPlayer, pass: number): number | null {
  const power = cardOf(cx, p)?.power;
  const rolls = rollsBy(cx, p, pass);
  if (!power || !SELF_ROLL.has(power) || rolls === 0) return null;
  return killOdds(power, null, rolls, heldOf(cx, p));
}

const pct = (x: number) => String(Math.round(x * 100));
const short = (name: string) => (name.length > 7 ? `${name.slice(0, 6)}…` : name);

const pairText = (o: Pair | null) => (o ? `${pct(o.natural)}/${pct(o.power)}` : '—');

/** The compact readout under a seat: one line per other player, `them / me`. */
export function SeatOdds({ cx, player: p, onOpen }: { cx: OddsContext; player: PublicPlayer; onOpen: () => void }) {
  const others = cx.view.players.filter((o) => o.id !== p.id && o.status !== 'out');
  const self = selfRollOdds(cx, p, cx.view.sequence + (hasGone(cx, p) ? 1 : 0));
  return (
    <button
      type="button"
      className="odds-mini"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      title="Kill odds, natural / with power (this turn). Click for every turn."
    >
      <span className="odds-head">
        <span>vs</span>
        <span>{short(p.name)} →</span>
        <span>→ {short(p.name)}</span>
      </span>
      {others.map((o) => {
        const first = cx.view.sequence + (hasGone(cx, p) ? 1 : 0);
        const out = pairOdds(cx, p, o, first);
        const back = pairOdds(cx, o, p, cx.view.sequence + (hasGone(cx, o) ? 1 : 0));
        return (
          <span key={o.id} className="odds-line">
            <span>{o.id === cx.view.you.id ? 'you' : short(o.name)}</span>
            <span>{pairText(out)}</span>
            <span>{pairText(back)}</span>
          </span>
        );
      })}
      {self !== null && (
        <span className="odds-line odds-self">
          <span>self-roll</span>
          <span>{pct(self)}</span>
          <span />
        </span>
      )}
    </button>
  );
}

/** Every pass left in the round, for one player attacking and being attacked. */
export function OddsModal({ cx, player: p, onClose }: { cx: OddsContext; player: PublicPlayer; onClose: () => void }) {
  const passes = Array.from(
    { length: Math.max(0, MAX_SEQUENCES - cx.view.sequence + 1) },
    (_, i) => cx.view.sequence + i,
  );
  const others = cx.view.players.filter((o) => o.id !== p.id && o.status !== 'out' && o.card !== null);
  const card = cardOf(cx, p);
  const selfRoll = card && SELF_ROLL.has(card.power);

  const head = (
    <tr>
      <th />
      {passes.map((n) => (
        <th key={n} colSpan={2}>
          Turn {n}
        </th>
      ))}
    </tr>
  );
  const sub = (
    <tr className="odds-sub">
      <th />
      {passes.flatMap((n) => [<th key={`${n}n`}>natural</th>, <th key={`${n}p`}>+ power</th>])}
    </tr>
  );
  const cells = (get: (pass: number) => Pair | null) =>
    passes.flatMap((n) => {
      const o = get(n);
      return o
        ? [
            <td key={`${n}n`}>{pct(o.natural)}%</td>,
            <td key={`${n}p`} className={o.power > o.natural + 0.005 ? 'odds-boost' : undefined}>
              {pct(o.power)}%
            </td>,
          ]
        : [
            <td key={`${n}n`} className="odds-none">
              —
            </td>,
            <td key={`${n}p`} className="odds-none">
              —
            </td>,
          ];
    });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal odds-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="row">
          <h3>
            <PName>{p.name}</PName>
            {card && <span className="muted small"> — {card.name}</span>}
          </h3>
          <span className="spacer" />
          <button onClick={onClose}>Close</button>
        </div>
        <p className="muted small">
          Tables show a player's chance to destroy a card by the end of each turn assuming optimal choices. Dice already
          held count as 'locked' until unheld.
        </p>

        <h4>{short(p.name)} attacking:</h4>
        <table className="odds-table">
          <thead>
            {head}
            {sub}
          </thead>
          <tbody>
            {others.map((o) => (
              <tr key={o.id}>
                <th>{o.id === cx.view.you.id ? 'You' : o.name}</th>
                {cells((n) => pairOdds(cx, p, o, n))}
              </tr>
            ))}
            {selfRoll && (
              <tr className="odds-self">
                <th>Self-roll</th>
                {passes.flatMap((n) => {
                  const v = selfRollOdds(cx, p, n);
                  return [
                    <td key={`${n}n`} colSpan={2}>
                      {v === null ? '—' : `${pct(v)}%`}
                    </td>,
                  ];
                })}
              </tr>
            )}
          </tbody>
        </table>

        <h4>Attacking {short(p.name)}:</h4>
        <table className="odds-table">
          <thead>
            {head}
            {sub}
          </thead>
          <tbody>
            {others.map((o) => (
              <tr key={o.id}>
                <th>{o.id === cx.view.you.id ? 'You' : o.name}</th>
                {cells((n) => pairOdds(cx, o, p, n))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
